// Cloudinary Spec Image Generator
// Add this to both calculators (glass and brick)

class SpecImageGenerator {
    constructor() {
        this.cloudName = 'dn1m7k0o0';
        this.baseUrl = `https://res.cloudinary.com/${this.cloudName}/image/upload`;
    }

    generateSpecUrl(calculatorData) {
        const { productType, shape, width, height, diameter, thickness, units, inputC, inputD, accessories, quantity } = calculatorData;
        
        // Convert all measurements to mm for consistency
        const widthMM = this.convertToMM(width, units);
        const heightMM = this.convertToMM(height, units);
        const diameterMM = this.convertToMM(diameter, units);
        const inputCMM = this.convertToMM(inputC, units);
        const inputDMM = this.convertToMM(inputD, units);
        
        // Store accessories and quantity for use in createTextOverlays
        this.currentAccessories = accessories || [];
        this.currentQuantity = quantity || 1;
        
        // Generate supplier code
        const supplierCode = this.generateSupplierCode(shape, widthMM, heightMM, diameterMM, inputCMM, inputDMM, thickness);
        
        // Only generate spec images for complex shapes that need visual clarification
        const complexShapes = ['both-top-corners-cut', 'single-corner-cut', 'all-corners-cut', 'sloped-top', 'trapezium'];
        let specUrl = null;
        
        if (complexShapes.includes(shape)) {
            // Create text overlays for complex shapes
            const overlays = this.createTextOverlays(shape, widthMM, heightMM, diameterMM, inputCMM, inputDMM, thickness, productType);
            
            // Build Cloudinary URL
            const shapeName = this.getCloudinaryShapeName(shape);
            
            // If accessories are present, add padding at bottom to accommodate them
            // Base image is ~200px tall, add 25px per accessory line
            let paddingTransform = '';
            if (this.currentAccessories && this.currentAccessories.length > 0) {
                const extraHeight = 200 + (this.currentAccessories.length * 25) + 20; // base + lines + margin
                // b_white = white background, c_pad = pad mode, h_ = height, g_north = anchor to top
                paddingTransform = `b_white,c_pad,h_${extraHeight},g_north/`;
            }
            
            const transformations = overlays.join('/');
            
            specUrl = `${this.baseUrl}/${paddingTransform}${transformations}/shapes/${shapeName}.png`;
        }
        
        return {
            supplierCode: supplierCode,
            specUrl: specUrl, // Will be null for simple shapes
            dimensions: this.formatDimensions(shape, widthMM, heightMM, diameterMM, inputCMM, inputDMM)
        };
    }

    convertToMM(value, units) {
        if (!value || value === 0) return 0;
        
        if (units === 'mm') return Math.round(value);
        if (units === 'cm') return Math.round(value * 10);
        if (units === 'in') return Math.round(value * 25.4);
        return Math.round(value);
    }

    generateSupplierCode(shape, widthMM, heightMM, diameterMM, inputCMM, inputDMM, thickness) {
        const shapeCodes = {
            'square-rectangular': 'RECT',
            'arched': 'ARCH', 
            'half-arch': 'HALF',
            'both-top-corners-cut': 'BTC',
            'single-corner-cut': 'SCC',
            'all-corners-cut': 'ACC',
            'sloped-top': 'SLT',
            'circular': 'CIRC',
            'trapezium': 'TRP'
        };

        const shapeCode = shapeCodes[shape] || 'CUST';
        const thickCode = thickness.replace('mm', '');

        if (shape === 'circular') {
            return `${shapeCode} D${diameterMM} T${thickCode}`;
        } else if (shape === 'square-rectangular') {
            return `${shapeCode} A${widthMM} B${heightMM} T${thickCode}`;
        } else if (['both-top-corners-cut', 'single-corner-cut', 'all-corners-cut'].includes(shape)) {
            return `${shapeCode} A${widthMM} B${heightMM} C${inputCMM} D${inputDMM} T${thickCode}`;
        } else if (shape === 'sloped-top') {
            return `${shapeCode} A${widthMM} B${heightMM} C${inputCMM} T${thickCode}`;
        } else if (shape === 'trapezium') {
            return `${shapeCode} A${widthMM} B${heightMM} C${inputCMM} T${thickCode}`;
        } else {
            return `${shapeCode} A${widthMM} B${heightMM} T${thickCode}`;
        }
    }

    createTextOverlays(shape, widthMM, heightMM, diameterMM, inputCMM, inputDMM, thickness, productType) {
        const overlays = [];
        const thickText = thickness.replace('mm', '') + 'mm';
        
        // Add "BRICK" label for brick products to avoid confusion with glass
        const thicknessLabel = productType === 'brick' ? `${thickText}%20BRICK` : `${thickText}%20thick`;

        // Base text style
        const textStyle = 'Arial_14_bold';
        const textColor = 'rgb:000000';
        
        // Add quantity in top-left corner (always show, helps supplier see at a glance)
        const qty = this.currentQuantity || 1;
        overlays.push(`l_text:Arial_14_bold:Qty%3A%20${qty},co_rgb:000000,g_north_west,x_10,y_10`);

        if (shape === 'circular') {
            // Diameter label at top
            overlays.push(`l_text:${textStyle}:D%3D${diameterMM}mm,co_${textColor},g_north,y_20`);
            // Thickness at bottom
            overlays.push(`l_text:${textStyle}:${thicknessLabel},co_${textColor},g_south,y_20`);
            
        } else if (shape === 'square-rectangular') {
            // Width at bottom
            overlays.push(`l_text:${textStyle}:A%3D${widthMM}mm,co_${textColor},g_south,y_20`);
            // Height on right
            overlays.push(`l_text:${textStyle}:B%3D${heightMM}mm,co_${textColor},g_east,x_20`);
            // Thickness at top
            overlays.push(`l_text:${textStyle}:${thicknessLabel},co_${textColor},g_north,y_10`);
            
        } else if (shape === 'both-top-corners-cut') {
            // A = Overall Width (bottom)
            overlays.push(`l_text:${textStyle}:A%3D${widthMM}mm,co_${textColor},g_south,y_20`);
            // B = Overall Height (right side, moved further right and rotated)
            overlays.push(`l_text:${textStyle}:B%3D${heightMM}mm,co_${textColor},g_east,x_35,a_90`);
            // C = Height to Cut-off (left side, moved further left and rotated)
            overlays.push(`l_text:${textStyle}:C%3D${inputCMM}mm,co_${textColor},g_west,x_35,a_90`);
            // D = Top Flat Width (top)
            overlays.push(`l_text:${textStyle}:D%3D${inputDMM}mm,co_${textColor},g_north,y_20`);
            // Thickness
            overlays.push(`l_text:${textStyle}:${thicknessLabel},co_${textColor},g_north_east,x_10,y_10`);
            
        } else if (shape === 'single-corner-cut') {
            // A = Overall Width (80px from left)
            overlays.push(`l_text:${textStyle}:A%3D${widthMM}mm,co_${textColor},g_south_west,x_80,y_20`);
            // B = Overall Height (230px from left, 70px down, rotated)
            overlays.push(`l_text:${textStyle}:B%3D${heightMM}mm,co_${textColor},g_north_west,x_230,y_70,a_90`);
            // C = Height to Cut-off (180px from left, 90px down, rotated)
            overlays.push(`l_text:${textStyle}:C%3D${inputCMM}mm,co_${textColor},g_north_west,x_180,y_90,a_90`);
            // D = Top Flat Width (60px from left)
            overlays.push(`l_text:${textStyle}:D%3D${inputDMM}mm,co_${textColor},g_north_west,x_60,y_20`);
            // Thickness (230px from left)
            overlays.push(`l_text:${textStyle}:${thicknessLabel},co_${textColor},g_north_west,x_230,y_10`);
            
        } else if (shape === 'all-corners-cut') {
            // A = Overall Width (bottom, moved down slightly)
            overlays.push(`l_text:${textStyle}:A%3D${widthMM}mm,co_${textColor},g_south,y_25`);
            // B = Overall Height (right side, moved right and down slightly)
            overlays.push(`l_text:${textStyle}:B%3D${heightMM}mm,co_${textColor},g_east,x_40,y_10,a_90`);
            // C = Corner Cut Vertical (left side, moved left and up slightly)
            overlays.push(`l_text:${textStyle}:C%3D${inputCMM}mm,co_${textColor},g_west,x_45,y_-5,a_90`);
            // D = Corner Cut Horizontal (top, moved left)
            overlays.push(`l_text:${textStyle}:D%3D${inputDMM}mm,co_${textColor},g_north_west,x_20,y_20`);
            // Thickness
            overlays.push(`l_text:${textStyle}:${thicknessLabel},co_${textColor},g_center`);
            
        } else if (shape === 'sloped-top') {
            // A = Bottom Width (bottom)
            overlays.push(`l_text:${textStyle}:A%3D${widthMM}mm,co_${textColor},g_south,y_20`);
            // B = Left Height (left side, moved further left and rotated)
            overlays.push(`l_text:${textStyle}:B%3D${heightMM}mm,co_${textColor},g_west,x_35,a_90`);
            // C = Right Height (right side, moved further right and rotated)
            overlays.push(`l_text:${textStyle}:C%3D${inputCMM}mm,co_${textColor},g_east,x_35,a_90`);
            // Thickness
            overlays.push(`l_text:${textStyle}:${thicknessLabel},co_${textColor},g_north,y_10`);
            
        } else if (shape === 'trapezium') {
            // A = Bottom Width (bottom)
            overlays.push(`l_text:${textStyle}:A%3D${widthMM}mm,co_${textColor},g_south,y_20`);
            // B = Height (right, vertical)
            overlays.push(`l_text:${textStyle}:B%3D${heightMM}mm,co_${textColor},g_east,x_20,a_90`);
            // C = Top Width (top)
            overlays.push(`l_text:${textStyle}:C%3D${inputCMM}mm,co_${textColor},g_north,y_20`);
            // Thickness
            overlays.push(`l_text:${textStyle}:${thicknessLabel},co_${textColor},g_west,x_20`);
            
        } else {
            // Default for arched, half-arch, etc.
            overlays.push(`l_text:${textStyle}:A%3D${widthMM}mm,co_${textColor},g_south,y_20`);
            overlays.push(`l_text:${textStyle}:B%3D${heightMM}mm,co_${textColor},g_east,x_20`);
            overlays.push(`l_text:${textStyle}:${thicknessLabel},co_${textColor},g_north,y_10`);
        }

        // Add accessories as separate lines at the bottom of the image
        if (this.currentAccessories && this.currentAccessories.length > 0) {
            // Each accessory on its own line for clarity
            this.currentAccessories.forEach((accessory, index) => {
                const yOffset = 10 + (index * 18); // 18px spacing between lines
                const encodedAccessory = encodeURIComponent(accessory).replace(/%20/g, '%20');
                // Position at bottom-left, stacking upward from bottom
                overlays.push(`l_text:Arial_12_bold:${encodedAccessory},co_rgb:CC0000,g_south_west,x_10,y_${yOffset}`);
            });
        }

        return overlays;
    }

    getCloudinaryShapeName(shape) {
        const shapeMap = {
            'both-top-corners-cut': 'actual-both-corners-cut',
            'single-corner-cut': 'final-single-corner-cut',
            'all-corners-cut': 'actual-all-corners-cut',
            'sloped-top': 'actual-sloped-top',
            'trapezium': 'actual-trapezium'
        };
        
        return shapeMap[shape] || null; // Return null for shapes that don't need spec images
    }

    formatDimensions(shape, widthMM, heightMM, diameterMM, inputCMM, inputDMM) {
        if (shape === 'circular') {
            return `D=${diameterMM}mm`;
        } else if (shape === 'square-rectangular') {
            return `A=${widthMM}mm, B=${heightMM}mm`;
        } else if (['both-top-corners-cut', 'single-corner-cut', 'all-corners-cut'].includes(shape)) {
            return `A=${widthMM}mm, B=${heightMM}mm, C=${inputCMM}mm, D=${inputDMM}mm`;
        } else if (shape === 'sloped-top') {
            return `A=${widthMM}mm, B=${heightMM}mm, C=${inputCMM}mm`;
        } else if (shape === 'trapezium') {
            return `A=${widthMM}mm, B=${heightMM}mm, C=${inputCMM}mm`;
        } else {
            return `A=${widthMM}mm, B=${heightMM}mm`;
        }
    }
}

// Export for use in calculators
window.SpecImageGenerator = SpecImageGenerator;